// Lucee's onCFCRequest/serializeJSON uppercases every struct key on the wire, no matter how the
// CFML source cased it (confirmed against webshared/controllers/eventPage.cfc's own source, which
// is all camelCase). ../../trackside/src/data/rcsApi.ts hits the same thing and works around it
// with an explicit per-key map — same approach here, just scoped to eventPage.cfc's own (much
// smaller, since this is a response shape chosen from scratch) set of fields.
//
// Deliberately NOT an automatic transform: an uppercased camelCase key is ambiguous
// ("EVENTNAME" could be "eventname" or "eventName"), so the map has to be explicit.
const KEY_MAP: Record<string, string> = {
  SUCCESS: 'success',
  ERROR: 'error',
  EVENT: 'event',
  CLASSES: 'classes',
  SPONSORS: 'sponsors',
  EXTRAS: 'extras',
  BROADCASTS: 'broadcasts',
  RELATEDEVENTS: 'relatedEvents',
  RACETIMEEVENTS: 'raceTimeEvents',
  FEATUREDEVENTS: 'featuredEvents',
  VIEWER: 'viewer',

  ID: 'id',
  NAME: 'name',
  DESCRIPTION: 'description',
  FLYERURL: 'flyerUrl',
  STREAMINGURL: 'streamingUrl',
  HIDEENTRIES: 'hideEntries',
  OPENPRACTICESTART: 'openPracticeStart',
  OPENPRACTICEEND: 'openPracticeEnd',
  CLOSEDPRACTICESTART: 'closedPracticeStart',
  QUALIFYINGSTART: 'qualifyingStart',
  MAINSSTART: 'mainsStart',
  HIDEHEATS: 'hideHeats',
  MANUALSCHEDULE: 'manualSchedule',
  TRACKNAME: 'trackName',
  ADDRESS1: 'address1',
  ADDRESS2: 'address2',
  CITY: 'city',
  STATE: 'state',
  COUNTRY: 'country',
  ZIP: 'zip',
  ALTVENUE: 'altVenue',
  ALTADDRESS1: 'altAddress1',
  ALTADDRESS2: 'altAddress2',
  ALTCITY: 'altCity',
  ALTSTATE: 'altState',
  ALTZIP: 'altZip',
  ALTCOUNTRY: 'altCountry',
  PHONE: 'phone',
  EMAIL: 'email',
  WWW: 'www',
  CONTACTFIRSTNAME: 'contactFirstName',
  CONTACTLASTNAME: 'contactLastName',
  PROMOTERFIRSTNAME: 'promoterFirstName',
  PROMOTERLASTNAME: 'promoterLastName',
  PROMOTERPHONE: 'promoterPhone',
  PROMOTERWWW: 'promoterWww',
  HOTELINFO: 'hotelInfo',
  HOBBYSHOP: 'hobbyShop',
  TRACKAMENITIES: 'trackAmenities',
  DOLLARTYPE: 'dollarType',
  SIGNUPWINDOWOPEN: 'signupWindowOpen',
  FULL: 'full',
  ENTRYLIMIT: 'entryLimit',
  ENTRYCOUNT: 'entryCount',
  RACERLIMIT: 'racerLimit',
  RACERCOUNT: 'racerCount',
  SIGNUPURL: 'signupUrl',
  CLASSCOST1: 'classCost1',
  CLASSCOST2: 'classCost2',
  CLASSCOST3: 'classCost3',
  CLASSCOST4: 'classCost4',
  OPENPAYMENTDT: 'openPaymentDT',
  CLOSEPAYMENTDT: 'closePaymentDT',
  ENTRYLINEDT: 'entryLineDT',

  TYPENAME: 'typeName',
  POWERTYPE: 'powerType',
  DISABLED: 'disabled',
  PRICEOVERRIDE: 'priceOverride',
  RULES: 'rules',
  ENTERED: 'entered',
  FORMAT: 'format',

  COCODE: 'coCode',
  CONAME: 'coName',
  LEVELNAME: 'levelName',
  LOGOURL: 'logoUrl',

  TYPE: 'type',
  PRICE: 'price',

  TITLE: 'title',
  MESSAGE: 'message',
  CRITICAL: 'critical',
  CREATEDT: 'createDT',

  TRACKID: 'trackId',
  STARTDT: 'startDT',
  ENDDT: 'endDT',
  VENUENAME: 'venueName',

  LOGGEDIN: 'loggedIn',
  FIRSTNAME: 'firstName',
  LASTNAME: 'lastName',
  TEAMMANAGER: 'teamManager',
  STRIPEMERCHANTID: 'stripeMerchantId',
  PROFILEPICURL: 'profilePicUrl',

  // getSignupState / addClassEntry / addEventExtra (SignupState shape)
  VEHICLES: 'vehicles',
  ENTEREDVEHICLEID: 'enteredVehicleId',
  AMOUNTDUE: 'amountDue',
  AMOUNT: 'amount',
  PAID: 'paid',
  BLOCKED: 'blocked',
  HASTRANSPONDER: 'hasTransponder',
  CLASSESPERRACER: 'classesPerRacer',
  TOTALENTRIES: 'totalEntries',
  PERSONALONLY: 'personalOnly',
  ADDED: 'added',
  OPTIONLABEL: 'optionLabel',
  DAYS: 'days',
  OPTIONS: 'options',
  LABEL: 'label',
  MAXDAYS: 'maxDays',
  REMAINING: 'remaining',
  CHECKOUTURL: 'checkoutUrl',
}

function toCamelKey(key: string): string {
  return KEY_MAP[key.toUpperCase()] ?? key
}

/** Recursively remaps every object key in a parsed eventPage.cfc response to camelCase. */
export function camelizeResponse<T>(value: unknown): T {
  if (Array.isArray(value)) {
    return value.map((item) => camelizeResponse(item)) as T
  }
  if (value !== null && typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      out[toCamelKey(key)] = camelizeResponse(val)
    }
    return out as T
  }
  return value as T
}
